// Claim Intelligence Package™ — Master Chapter Registry
// ---------------------------------------------------------------
// SINGLE SOURCE OF TRUTH for every customer-facing chapter reference
// in the report. If any other file declares a chapter number, title,
// variant, or PDF filename token, that is a bug — route it through
// this module instead.
//
// Customer-facing numbering is reading order (1..12). Internal DOM /
// PDF anchors (`chapter-01` … `chapter-12`) and database IDs are
// preserved to keep deep links, bookmarks, and cross-page URLs stable.
//
// Presentation only. This module never touches business logic,
// calculations, evidence, or scoring.

import {
  FileText,
  BarChart3,
  Scale,
  Landmark,
  Car,
  ShieldCheck,
  Building2,
  Clock3,
  BookOpenCheck,
  ClipboardCheck,
  Handshake,
  BadgeCheck,
  type LucideIcon,
} from "lucide-react";
import type { DossierVariant } from "@/components/report/dossier-chapter";

export interface ChapterEntry {
  /** Sole customer-facing chapter number, 1..TOTAL_CHAPTERS. */
  displayNumber: number;
  /** Zero-padded chapter number, e.g. "03". */
  paddedDisplayNumber: string;
  /** Total number of chapters in the reading order. Same on every entry. */
  totalChapters: number;
  /** Sequential part number (1 = Executive, 5 = Certification). */
  partNumber: number;
  /** Roman numeral for the part, "I".."V". */
  partRoman: string;
  /** Human-readable part name for TOC grouping. */
  partName: string;
  /** Official long title — header, TOC, PDF header. */
  title: string;
  /** Short nav / mobile-jump label. */
  shortTitle: string;
  /** Standalone-PDF running-head title. */
  exportTitle: string;
  /** PDF running header title (multi-page). */
  runningHeader: string;
  /** Confidentiality-footer chapter title. */
  footerTitle: string;
  /** Stable slug for code-level cross-references. */
  internalId: string;
  /** Legacy DOM/PDF anchor id, e.g. "chapter-02". Never rendered. */
  anchorId: string;
  /** Filename token, e.g. "Executive_Decision_Cheat_Sheet". */
  pdfSlug: string;
  /** DossierChapter visual variant. */
  variant: DossierVariant;
  /**
   * Header "Category" descriptor. Replaces the previous "Status" misuse
   * where the number was echoed. Never contains the chapter number.
   */
  category: string;
  /** Decorative Lucide icon representing the chapter. Never null; falls back to FileText. */
  icon: LucideIcon;
}

const ROMAN = ["I", "II", "III", "IV", "V"] as const;

interface ChapterInput {
  partNumber: number;
  partName: string;
  title: string;
  shortTitle?: string;
  exportTitle?: string;
  runningHeader?: string;
  footerTitle?: string;
  internalId: string;
  anchorId: string;
  pdfSlug: string;
  variant: DossierVariant;
  category: string;
  icon?: LucideIcon;
}

/**
 * Reading-order chapter definitions. The array index (+1) determines
 * `displayNumber`. Anchors and internal IDs stay stable; only the
 * order in this array can shift customer-facing numbering.
 */
const CHAPTER_INPUTS: readonly ChapterInput[] = [
  // Part I · Executive
  {
    partNumber: 1,
    partName: "Executive",
    title: "Case Brief & Invoice Overview",
    shortTitle: "Case Brief & Invoice Overview",
    internalId: "case-brief",
    anchorId: "chapter-02",
    pdfSlug: "Case_Brief_and_Invoice_Overview",
    variant: "brief",
    category: "Case Brief",
    icon: FileText,
  },
  // Part II · Investigation
  {
    partNumber: 2,
    partName: "Investigation",
    title: "Market Benchmark Analysis",
    internalId: "market",
    anchorId: "chapter-03",
    pdfSlug: "Market_Benchmark_Analysis",
    variant: "market",
    category: "Market Benchmark",
    icon: BarChart3,
  },
  // Part III · Regulatory & Legal
  {
    partNumber: 3,
    partName: "Regulatory & Legal",
    title: "State Law Analysis",
    internalId: "state-law",
    anchorId: "chapter-04",
    pdfSlug: "State_Law_Analysis",
    variant: "legal",
    category: "Legal Research",
    icon: Scale,
  },
  {
    partNumber: 3,
    partName: "Regulatory & Legal",
    title: "Regulation Analysis",
    internalId: "regulation",
    anchorId: "chapter-06",
    pdfSlug: "Regulation_Analysis",
    variant: "regulatory",
    category: "Regulatory Reference",
    icon: Landmark,
  },
  {
    partNumber: 3,
    partName: "Regulatory & Legal",
    title: "Vehicle Ownership, Lien & Abandonment Rights Analysis",
    shortTitle: "Vehicle Ownership, Lien & Abandonment",
    runningHeader: "Vehicle Ownership & Lien",
    internalId: "ownership-lien",
    anchorId: "chapter-05",
    pdfSlug: "Vehicle_Ownership_Lien_and_Abandonment_Rights_Analysis",
    variant: "legal",
    category: "Legal Research",
    icon: Car,
  },
  {
    partNumber: 3,
    partName: "Regulatory & Legal",
    title: "Carrier Guideline Analysis",
    internalId: "carrier",
    anchorId: "chapter-07",
    pdfSlug: "Carrier_Guideline_Analysis",
    variant: "policy",
    category: "Policy Compliance",
    icon: ShieldCheck,
  },
  {
    partNumber: 3,
    partName: "Regulatory & Legal",
    title: "Vendor Intelligence",
    internalId: "vendor",
    anchorId: "chapter-08",
    pdfSlug: "Vendor_Intelligence",
    variant: "investigative",
    category: "Investigative Profile",
    icon: Building2,
  },
  {
    partNumber: 3,
    partName: "Regulatory & Legal",
    title: "Case Timeline & Key Events",
    internalId: "timeline",
    anchorId: "chapter-09",
    pdfSlug: "Case_Timeline_and_Key_Events",
    variant: "timeline",
    category: "Investigation Timeline",
    icon: Clock3,
  },
  {
    partNumber: 3,
    partName: "Regulatory & Legal",
    title: "Supporting Evidence & References",
    internalId: "supporting-evidence",
    anchorId: "chapter-11",
    pdfSlug: "Supporting_Evidence_and_References",
    variant: "appendix",
    category: "Supporting Evidence",
    icon: BookOpenCheck,
  },
  // Part IV · Negotiation
  {
    partNumber: 4,
    partName: "Negotiation",
    title: "Executive Decision Cheat Sheet",
    internalId: "executive-decision",
    anchorId: "chapter-01",
    pdfSlug: "Executive_Decision_Cheat_Sheet",
    variant: "executive",
    category: "Executive Decision",
    icon: ClipboardCheck,
  },
  {
    partNumber: 4,
    partName: "Negotiation",
    title: "Negotiation Strategy",
    internalId: "negotiation-strategy",
    anchorId: "chapter-10",
    pdfSlug: "Negotiation_Strategy",
    variant: "playbook",
    category: "Negotiation Strategy",
    icon: Handshake,
  },
  // Part V · Certification
  {
    partNumber: 5,
    partName: "Certification",
    title: "Professional Review Conclusion",
    internalId: "conclusion",
    anchorId: "chapter-12",
    pdfSlug: "Professional_Review_Conclusion",
    variant: "certificate",
    category: "Professional Certification",
    icon: BadgeCheck,
  },
];

export const TOTAL_CHAPTERS = CHAPTER_INPUTS.length;

function expand(input: ChapterInput, idx: number): ChapterEntry {
  const displayNumber = idx + 1;
  return Object.freeze({
    displayNumber,
    paddedDisplayNumber: String(displayNumber).padStart(2, "0"),
    totalChapters: TOTAL_CHAPTERS,
    partNumber: input.partNumber,
    partRoman: ROMAN[input.partNumber - 1] ?? String(input.partNumber),
    partName: input.partName,
    title: input.title,
    shortTitle: input.shortTitle ?? input.title,
    exportTitle: input.exportTitle ?? input.title,
    runningHeader: input.runningHeader ?? input.shortTitle ?? input.title,
    footerTitle: input.footerTitle ?? input.title,
    internalId: input.internalId,
    anchorId: input.anchorId,
    pdfSlug: input.pdfSlug,
    variant: input.variant,
    category: input.category,
    icon: input.icon ?? FileText,
  });
}

export const CHAPTERS: readonly ChapterEntry[] = Object.freeze(
  CHAPTER_INPUTS.map(expand),
);

// Lookup indexes ------------------------------------------------------
const BY_ANCHOR = new Map<string, ChapterEntry>();
const BY_INTERNAL = new Map<string, ChapterEntry>();
for (const c of CHAPTERS) {
  BY_ANCHOR.set(c.anchorId, c);
  BY_INTERNAL.set(c.internalId, c);
}

/**
 * Resolve a chapter from any of: internalId, anchorId, or anchor href
 * (`#chapter-XX`). Throws if unknown so mis-references fail loudly at
 * dev time rather than silently rendering the wrong chapter.
 */
export function getChapter(idOrAnchor: string): ChapterEntry {
  const key = idOrAnchor.startsWith("#") ? idOrAnchor.slice(1) : idOrAnchor;
  const hit = BY_ANCHOR.get(key) ?? BY_INTERNAL.get(key);
  if (!hit) {
    throw new Error(`chapter-registry: unknown chapter "${idOrAnchor}"`);
  }
  return hit;
}

export function tryGetChapter(idOrAnchor: string | null | undefined): ChapterEntry | null {
  if (!idOrAnchor) return null;
  const key = idOrAnchor.startsWith("#") ? idOrAnchor.slice(1) : idOrAnchor;
  return BY_ANCHOR.get(key) ?? BY_INTERNAL.get(key) ?? null;
}

export function getChapterByInternalId(id: string): ChapterEntry {
  const hit = BY_INTERNAL.get(id);
  if (!hit) {
    throw new Error(`chapter-registry: unknown internalId "${id}"`);
  }
  return hit;
}

export function chapterHref(idOrAnchor: string): string {
  return `#${getChapter(idOrAnchor).anchorId}`;
}

/** "Chapter 3" — customer-facing short reference. */
export function chapterRef(idOrAnchor: string): string {
  return `Chapter ${getChapter(idOrAnchor).displayNumber}`;
}

/** "Chapter 3 — State Law Analysis". */
export function chapterLongRef(idOrAnchor: string): string {
  const c = getChapter(idOrAnchor);
  return `Chapter ${c.displayNumber} — ${c.title}`;
}

export function nextChapter(idOrAnchor: string): ChapterEntry | null {
  const c = getChapter(idOrAnchor);
  const idx = c.displayNumber - 1;
  return CHAPTERS[idx + 1] ?? null;
}

/** Drop-in replacement for the legacy `nextChapterLabelAfter` helper. */
export function nextChapterLabel(idOrAnchor: string): string | null {
  return nextChapter(idOrAnchor)?.title ?? null;
}

export interface PartGroup {
  partNumber: number;
  partRoman: string;
  partName: string;
  chapters: ChapterEntry[];
}

/** Group chapters by part, preserving reading order within each part. */
export function partsGrouped(): PartGroup[] {
  const out: PartGroup[] = [];
  for (const c of CHAPTERS) {
    const last = out[out.length - 1];
    if (last && last.partNumber === c.partNumber) {
      last.chapters.push(c);
    } else {
      out.push({
        partNumber: c.partNumber,
        partRoman: c.partRoman,
        partName: c.partName,
        chapters: [c],
      });
    }
  }
  return out;
}
